import { Navigate, useLocation } from "react-router-dom"
import { Loader2 } from "lucide-react"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { useAuth } from "@/hooks/use-auth"
import { injectOrgSlug, resolveOrgSlug } from "@/lib/inject-org-slug"

/**
 * Capte les chemins d'application dépourvus de préfixe orgSlug (deep-link
 * partagé par email, chat, notification Signal) et les redirige :
 *   - non connecté → `/login?redirect=<path>` (LoginPage injectera l'orgSlug
 *     post-authentification)
 *   - connecté → `/<orgSlug><path>` immédiat, query + hash préservés
 *
 * Monté dans App.tsx sur `/<APP_PATH_PREFIX>/*` pour chaque préfixe d'app
 * (segment litéral qui bat en rank React Router le parasitage `/:orgSlug`).
 * Rien à faire ici pour distinguer public/app : c'est le placement des Routes
 * dans App.tsx qui garantit qu'on n'est monté que sur des chemins d'app.
 */
export function BarePathRedirect() {
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  const fullPath = `${location.pathname}${location.search}${location.hash}`

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(fullPath)}`}
        replace
      />
    )
  }

  return pipe(
    resolveOrgSlug(user),
    O.match(
      // Cas limite : user auth sans slug résolvable (backend qui n'aurait pas
      // hydraté tenant_code, ou logout partiel). Retour à /login pour une
      // reconnexion propre plutôt qu'une URL corrompue.
      () => <Navigate to="/login" replace />,
      (slug) => <Navigate to={injectOrgSlug(fullPath, slug)} replace />,
    ),
  )
}
